// src/screens/ItemDetailScreen.js - REVISED WITH DELETION AND REDIRECT FIX
import React, { useState, useEffect, useRef } from 'react';
import { doc, onSnapshot, serverTimestamp, updateDoc, deleteDoc } from 'firebase/firestore';
import { LocationIcon, FlagIcon, ShareIcon } from '../components/Icons';
import ReportModal from '../components/ReportModal';
import { getStorage, ref, deleteObject } from 'firebase/storage';
import { db, callable } from '../firebase';
import { getGeoFromCEP, haversineDistance } from '../utils/helpers';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const LoadingComponent = () => (
    <div className="flex justify-center items-center h-screen">
        <div className="text-xl text-gray-600">Carregando item...</div>
    </div>
);

export default function ItemDetailScreen({ setModal }) {
    // --- Hooks ---
    const { itemId } = useParams();
    const navigate = useNavigate();
    const { user, profile, loading: authLoading, validationStatus } = useAuth();
    
    // ✅ FIX: Added a ref to track if navigation has been triggered.
    const hasNavigatedAway = useRef(false);

    // --- State Management ---
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isOwner, setIsOwner] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [showReportModal, setShowReportModal] = useState(false);
    const [calculatedDistance, setCalculatedDistance] = useState(null);
    const [isUsingDefaultCEP, setIsUsingDefaultCEP] = useState(false);

    // Effect to fetch the item's data
    useEffect(() => {
        setLoading(true);
        const itemRef = doc(db, 'items', itemId);
        const unsubscribe = onSnapshot(itemRef, (docSnap) => {
            if (docSnap.exists()) {
                setItem({ id: docSnap.id, ...docSnap.data() });
            } else {
                // ✅ FIX: If a deletion is in progress on this screen (indicated by the ref),
                // do nothing. The handleDeleteItem function is in control.
                // Otherwise, it was deleted elsewhere, so proceed with setting item to null.
                if (!hasNavigatedAway.current) {
                    console.error("Item not found (deleted externally)!");
                    setItem(null);
                }
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, [itemId]); // Dependency array is correct, no need to add the ref here.

    // ✅ NEW: Effect to handle redirection if the item is deleted by another process.
    useEffect(() => {
        // Wait until loading is complete.
        if (!loading && !item && !hasNavigatedAway.current) {
            hasNavigatedAway.current = true;
            setModal({
                title: "Item Indisponível",
                message: "Este item não está mais disponível.",
                onConfirm: () => {
                    setModal(null);
                    navigate('/market');
                }
            });
        }
    }, [loading, item, navigate, setModal]);

    // Effect to determine ownership and calculate distance
    useEffect(() => {
        if (item && profile) {
            setIsOwner(item.ownerId === profile.id);
        } else {
            setIsOwner(false);
        }

        const calculateAndSetDistance = async () => {
            if (!item?.location?.lat || !item?.location?.lon) {
                setCalculatedDistance(null);
                return;
            }
            try {
                let userLocation = null;
                let usingDefault = false;
                if (profile?.location?.lat && profile?.location?.lon) {
                    userLocation = { lat: profile.location.lat, lon: profile.location.lon };
                } else if (profile?.cep) {
                    userLocation = await getGeoFromCEP(profile.cep);
                } else {
                    userLocation = await getGeoFromCEP('01311-000'); // Fallback
                    usingDefault = true;
                }
                const itemLocation = { lat: item.location.lat, lon: item.location.lon };
                if (userLocation && itemLocation) {
                    const distance = haversineDistance(userLocation, itemLocation);
                    setCalculatedDistance(distance);
                    setIsUsingDefaultCEP(usingDefault && !!profile);
                }
            } catch (error) {
                console.error('Erro ao calcular distância:', error);
                setCalculatedDistance(null);
            }
        };

        if (item) {
            calculateAndSetDistance();
        }

    }, [item, profile]);

    // --- Handlers for user actions ---
    const handleShare = async () => {
        const shareUrl = window.location.href;
        const shareData = {
            title: `Confira este item: ${item.title}`,
            text: `Eu vi "${item.title}" no Escambo Digital e achei que você se interessaria! Veja aqui:`,
            url: shareUrl,
        };
        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                navigator.clipboard.writeText(shareUrl);
                setModal({
                    title: "Link Copiado",
                    message: "Link do item copiado para a área de transferência!",
                });
            }
        } catch (err) {
            console.error("Error sharing:", err);
        }
    };

    const handleInitiateTrade = async () => {
        if (!profile || validationStatus !== 'success') {
            setModal({ title: "Login Necessário", message: "Você precisa estar logado e validado para iniciar uma troca." });
            return;
        }

        const creditCost = Number(item.credits || 0);
        const userBalance = Number(profile.credits || 0);

        if (userBalance < creditCost) {
            setModal({ title: "Créditos Insuficientes", message: `Você precisa de ${creditCost} créditos. Você tem apenas ${userBalance}.` });
            return;
        }

        setModal({
            title: "Confirmar Troca",
            message: `Você confirma a troca deste item por ${item.credits} créditos?`,
            actions: [
                 // ✅ FIX: Standardized to use 'label' and 'style'.
                {
                    label: "Cancelar",
                    onClick: () => setModal(null),
                    style: 'secondary'
                },
                {
                    label: "Sim, Trocar",
                    style: 'primary',
                    onClick: async () => {
                        setModal({ title: "Processando Troca", message: "Aguarde...", isLoading: true });
                        try {
                            const initiateTrade = callable('initiateTrade');

			    // 💡 NOVO LOG: Verifica os valores antes de enviar
                            console.log("--- Payload Data Check ---");
                            console.log("item.id:", item.id, " (Type:", typeof item.id, ")");
                            console.log("item.credits (raw):", item.credits, " (Type:", typeof item.credits, ")");
                            console.log("creditCost (sent):", creditCost, " (Type:", typeof creditCost, ")");
                            console.log("--------------------------");

                            await initiateTrade({ itemId: item.id, credits: creditCost });
                            setModal({
                                title: "Troca Iniciada",
                                message: "Sucesso! Você será redirecionado para suas trocas.",
                                onConfirm: () => {
                                    setModal(null);
                                    navigate('/trades', { state: { initialTab: 'status' } });
                                }
                            });
                        } catch (error) {
                            console.error("Erro na troca:", error);
                            setModal({ title: "Erro na Troca", message: `Ocorreu um problema: ${error.message}` });
                        }
                    }
                }
            ]
        });
    };

    const handleReportSubmit = async (reason) => {
        if (!item || !profile) return;
        const itemRef = doc(db, "items", item.id);
        try {
            await updateDoc(itemRef, {
                isReported: true,
                reportDetails: {
                    reason: reason,
                    reportedBy: profile.id,
                    reporterEmail: profile.email,
                    reportedAt: serverTimestamp()
                }
            });
            setShowReportModal(false);
            setModal({
                title: "Denúncia Enviada",
                message: "Sua denúncia foi enviada. Nossa equipe irá analisar o caso. Obrigado!",
                onConfirm: () => setModal(null)
            });
        } catch (error) {
            setModal({
                title: "Erro",
                message: "Erro ao enviar denúncia. Tente novamente.",
                onConfirm: () => setModal(null)
            });
        }
    };

    // ✅ REVISED: This handler now takes full control of the post-deletion flow.
    const handleDeleteItem = async () => {
        setModal({
            title: "Confirmar Exclusão",
            message: "Tem certeza que deseja deletar este item permanentemente? Esta ação não pode ser desfeita.",
            actions: [
                {
                    label: "Cancelar",
                    onClick: () => setModal(null),
                    style: 'secondary'
                },
                {
                    label: "Sim, Deletar",
                    style: 'danger',
                    onClick: async () => {
                        // Set the flag immediately to prevent the other useEffect from firing.
                        hasNavigatedAway.current = true;
                        setModal({ message: "Deletando item...", isLoading: true });

                        const itemRef = doc(db, 'items', item.id);
                        const storage = getStorage();
                        try {
                            if (item.imageUrls && item.imageUrls.length > 0) {
                                await Promise.all(item.imageUrls.map(url => deleteObject(ref(storage, url))));
                            }
                            await deleteDoc(itemRef);

                            setModal({
                                title: "Item Deletado",
                                message: "Item deletado com sucesso.",
                                onConfirm: () => {
                                    setModal(null);
                                    navigate('/market');
                                }
                            });
                        } catch (error) {
                            console.error("Erro ao deletar:", error);
                            // Reset the flag if the deletion fails.
                            hasNavigatedAway.current = false;
                            setModal({
                                title: "Erro",
                                message: "Erro ao deletar o item. Tente novamente.",
                                onConfirm: () => setModal(null)
                            });
                        }
                    }
                }
            ]
        });
    };
    
    // Guard Clauses
    if (authLoading || loading) {
        return <LoadingComponent />;
    }

    // After loading, if the item is still null (and we haven't already triggered a navigation),
    // it means the item doesn't exist. We show a loading component briefly while the redirect useEffect kicks in.
    if (!item) {
        return <LoadingComponent />;
    }
    
    // Listing held by the photo check (functions/lib/moderation.js): only
    // the owner and admins see it until an admin reviews it.
    if (item.status === 'bloqueado_moderacao' && user?.uid !== item.ownerId && profile?.role !== 'admin') {
        return <div className="max-w-xl mx-auto p-6 mt-8 bg-white rounded-lg shadow text-center"><h1 className="text-xl font-bold text-gray-800">Anúncio em análise</h1><p className="text-gray-600 mt-2">Este anúncio está sendo revisado e não está disponível no momento.</p></div>;
    }

    const imageUrls = item.imageUrls || [];

    const renderActionSection = () => {
        if (isOwner) {
            return (
                <div className="space-y-3">
                    <button onClick={() => navigate(`/edit-item/${item.id}`)} className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700">
                        Editar Item
                    </button>
                    <button onClick={handleDeleteItem} className="w-full bg-red-600 text-white font-bold py-3 rounded-lg hover:bg-red-700">
                        Deletar Item
                    </button>
                </div>
            );
        }

        if (validationStatus === 'success' && profile) {
             const hasEnoughCredits = (profile.credits || 0) >= item.credits;
             const creditsColorClass = hasEnoughCredits ? 'text-green-600' : 'text-red-600';
             return (
                   <div className="flex flex-col gap-4 mt-4">
                         <div className="text-center md:text-left flex-grow">
                               <p className="text-gray-500">
                                     Você tem <span className={`font-bold ${creditsColorClass}`}>{profile.credits || 0} créditos</span>
                               </p>
                               
                               {calculatedDistance !== null && (
                                     <div>
                                         <p className="text-xs text-gray-500 mt-1">
                                             Está a aprox: {calculatedDistance.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km de distância
                                         </p>
                                         {isUsingDefaultCEP && (
                                               <p
                                                   className="text-xs text-blue-600 mt-1 cursor-pointer hover:underline"
                                                   onClick={() => navigate('/profile')}
                                               >
                                                   Atualize seu CEP no perfil para maior precisão.
                                               </p>
                                         )}
                                     </div>
                               )}
                         </div>
                         <button
                               onClick={handleInitiateTrade}
                               disabled={!hasEnoughCredits}
                               className="w-full bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                         >
                               {hasEnoughCredits ? 'Trocar Agora' : 'Créditos Insuficientes'}
                         </button>
                   </div>
             );
        }

        return (
            <button onClick={() => navigate('/login')} className="w-full bg-gray-600 text-white font-bold py-3 rounded-lg hover:bg-gray-700">
                Faça Login para Trocar
            </button>
        )
    };

    return (
        <div className="max-w-4xl mx-auto p-4">
            {showReportModal && (
                <ReportModal
                    onClose={() => setShowReportModal(false)}
// ... existing a-z
                    onSubmit={handleReportSubmit}
                    itemName={item.title}
                />
            )}

            <button onClick={() => navigate('/market')} className="text-green-600 mb-4 inline-flex items-center">&larr; <span className="ml-2">Voltar ao Escambo Center</span></button>
            <div className="md:grid md:grid-cols-2 md:gap-8">
                <div>
                    {/* Square box reserved before the photo loads (no layout shift); whole photo visible. */}
                    <img
                        src={item.mediumUrls?.[selectedImageIndex] || imageUrls[selectedImageIndex] || 'https://placehold.co/600x400/e2e8f0/64748b?text=Item'}
                        alt={item.title}
                        width="600" height="600" fetchpriority="high" decoding="async"
                        className="w-full aspect-square object-contain bg-gray-100 rounded-lg mb-2"
                    />
                    {imageUrls.length > 1 && (
                        <div className="flex space-x-2">
                            {imageUrls.map((url, index) => (
                                <img
                                    key={index}
                                    src={item.thumbUrls?.[index] || url}
                                    alt={`Foto ${index + 1}`}
                                    width="64" height="64" loading="lazy"
                                    onClick={() => setSelectedImageIndex(index)}
                                    className={`w-16 h-16 object-cover rounded-md cursor-pointer border-2 ${selectedImageIndex === index ? 'border-green-500' : 'border-transparent'}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md mt-4 md:mt-0 relative">
                    <div className="absolute top-4 right-4 flex items-center space-x-3">
                        <button onClick={handleShare} className="text-gray-500 hover:text-green-600 transition-colors duration-200" title="Compartilhar item">
                            <ShareIcon className="w-6 h-6" />
                        </button>
                        {profile && !isOwner && (
                            <button onClick={() => setShowReportModal(true)} className="text-gray-500 hover:text-red-600 transition-colors duration-200" title="Denunciar este item">
                                <FlagIcon className="w-6 h-6" />
                            </button>
                        )}
                    </div>

                    <h1 className="text-3xl font-bold text-gray-800 pr-24">{item.title}</h1>
                    
                    {item.condition && (
                        <span className={`inline-block text-xs font-semibold mr-2 px-2.5 py-0.5 rounded-full ${item.condition === 'new' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                            {item.condition === 'new' ? 'Novo' : 'Usado'}
                        </span>
                    )}

                    <p className="text-2xl font-bold text-green-600 my-2">{item.credits} Créditos</p>
                    {item.category && <span className="inline-block bg-gray-200 rounded-full px-3 py-1 text-sm font-semibold text-gray-700 mr-2 mb-2">{item.category}</span>}
                    
                    {item.location?.city && (
                        <p className="text-sm text-gray-500 my-4 flex items-center">
                            <LocationIcon className="w-4 h-4 mr-2" />
                            {item.location.city}, {item.location.uf}
                        </p>
                    )}

                    <p className="text-gray-600 mb-6">{item.description}</p>
                    
                    {renderActionSection()}
                </div>
            </div>
        </div>
    );
}


